/**
 * String utilities for safe string operations
 * Prevents TypeError when calling string methods on non-string values
 */

/**
 * Safely converts a value to lowercase
 * @param {any} value - The value to convert
 * @param {string} fallback - Fallback value if conversion fails (default: '')
 * @returns {string} - Lowercase string or fallback
 */
export const safeToLowerCase = (value, fallback = '') => {
    try {
        if (value && typeof value === 'string') {
            return value.toLowerCase();
        }
        return fallback;
    } catch (error) {
        console.warn('Error converting to lowercase:', error);
        return fallback;
    }
};

/**
 * Safely trims a string
 * @param {any} value - The value to trim
 * @param {string} fallback - Fallback value if trimming fails (default: '')
 * @returns {string} - Trimmed string or fallback
 */
export const safeTrim = (value, fallback = '') => {
    try {
        if (value && typeof value === 'string') {
            return value.trim();
        }
        return fallback;
    } catch (error) {
        console.warn('Error trimming string:', error);
        return fallback;
    }
};

/**
 * Safely checks if a string includes another string
 * @param {any} haystack - The string to search in
 * @param {any} needle - The string to search for
 * @param {boolean} caseSensitive - Whether to do case-sensitive search (default: false)
 * @returns {boolean} - True if haystack includes needle
 */
export const safeIncludes = (haystack, needle, caseSensitive = false) => {
    try {
        if (!haystack || !needle) return false;
        
        const haystackStr = typeof haystack === 'string' ? haystack : String(haystack);
        const needleStr = typeof needle === 'string' ? needle : String(needle);
        
        if (caseSensitive) {
            return haystackStr.includes(needleStr);
        } else {
            return safeToLowerCase(haystackStr).includes(safeToLowerCase(needleStr));
        }
    } catch (error) {
        console.warn('Error checking string includes:', error);
        return false;
    }
};

/**
 * Safely gets a property from an object and converts it to lowercase
 * @param {any} obj - The object to get property from
 * @param {string} property - The property name
 * @param {string} fallback - Fallback value if property doesn't exist (default: '')
 * @returns {string} - Lowercase property value or fallback
 */
export const safeGetLowercaseProperty = (obj, property, fallback = '') => {
    try {
        if (obj && typeof obj === 'object' && obj[property]) {
            return safeToLowerCase(obj[property], fallback);
        }
        return fallback;
    } catch (error) {
        console.warn('Error getting lowercase property:', error);
        return fallback;
    }
};

/**
 * Safely filters an array based on string property matching
 * @param {Array} array - The array to filter
 * @param {string} property - The property to check
 * @param {string} searchTerm - The term to search for
 * @param {boolean} caseSensitive - Whether to do case-sensitive search (default: false)
 * @returns {Array} - Filtered array
 */
export const safeFilterByStringProperty = (array, property, searchTerm, caseSensitive = false) => {
    try {
        if (!Array.isArray(array) || !searchTerm) return array || [];
        
        return array.filter(item => {
            if (!item || typeof item !== 'object') return false;
            
            const propertyValue = item[property];
            if (!propertyValue) return false;
            
            return safeIncludes(propertyValue, searchTerm, caseSensitive);
        });
    } catch (error) {
        console.warn('Error filtering array by string property:', error);
        return array || [];
    }
};

/**
 * Safely checks if a value is a valid string
 * @param {any} value - The value to check
 * @returns {boolean} - True if value is a valid string
 */
export const isValidString = (value) => {
    return value && typeof value === 'string' && value.length > 0;
};

/**
 * Safely concatenates strings
 * @param {...any} values - Values to concatenate
 * @returns {string} - Concatenated string
 */
export const safeConcat = (...values) => {
    try {
        return values
            .filter(value => value != null)
            .map(value => String(value))
            .join('');
    } catch (error) {
        console.warn('Error concatenating strings:', error);
        return '';
    }
};

/**
 * Safely replaces text in a string
 * @param {any} str - The string to replace in
 * @param {string|RegExp} searchValue - The value to search for
 * @param {string} replaceValue - The value to replace with
 * @param {string} fallback - Fallback value if replacement fails
 * @returns {string} - String with replacements or fallback
 */
export const safeReplace = (str, searchValue, replaceValue, fallback = '') => {
    try {
        if (!str || typeof str !== 'string') return fallback;
        return str.replace(searchValue, replaceValue);
    } catch (error) {
        console.warn('Error replacing string:', error);
        return fallback;
    }
};

/**
 * Safely splits a string
 * @param {any} str - The string to split
 * @param {string|RegExp} separator - The separator to split on
 * @param {Array} fallback - Fallback array if splitting fails (default: [])
 * @returns {Array} - Array of split strings or fallback
 */
export const safeSplit = (str, separator, fallback = []) => {
    try {
        if (!str || typeof str !== 'string') return fallback;
        return str.split(separator);
    } catch (error) {
        console.warn('Error splitting string:', error);
        return fallback;
    }
};

export default {
    safeToLowerCase,
    safeTrim,
    safeIncludes,
    safeGetLowercaseProperty,
    safeFilterByStringProperty,
    isValidString,
    safeConcat,
    safeReplace,
    safeSplit
};
